import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { SEO, ArticleSchema } from '../components/SEO';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', { 
    day: 'numeric',
    month: 'long', 
    year: 'numeric' 
  });
};

// LinkedIn Icon Component
const LinkedInIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="16" 
    height="16" 
    viewBox="0 0 24 24" 
    fill="currentColor"
    className="inline-block"
  >
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
  </svg>
);

export default function ArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/articles/${slug}`);
        setArticle(response.data);
        setError(null);
      } catch (err) {
        logger.error('Error fetching article:', err);
        setError('Article not found');
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen py-24 bg-white">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <div className="animate-pulse">
              <div className="h-4 bg-[#e5e2dc] rounded w-1/4 mb-4"></div>
              <div className="h-12 bg-[#e5e2dc] rounded w-full mb-4"></div>
              <div className="h-12 bg-[#e5e2dc] rounded w-2/3 mb-8"></div>
              <div className="h-4 bg-[#e5e2dc] rounded w-full mb-3"></div>
              <div className="h-4 bg-[#e5e2dc] rounded w-full mb-3"></div>
              <div className="h-4 bg-[#e5e2dc] rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !article) {
    return (
      <Layout>
        <div className="min-h-screen py-24 bg-white">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="font-playfair text-4xl font-bold text-[#1a1a1a] mb-4">
              Article Not Found
            </h1>
            <p className="text-[#4a4744] mb-8">
              The article you're looking for doesn't exist or has been removed.
            </p>
            <Link 
              to="/insights" 
              className="btn-primary inline-block"
            >
              Back to Insights
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO 
        title={article.title}
        path={`/insights/${slug}`}
        description={article.excerpt}
        type="article"
        article={{
          publishedTime: article.publication_date || article.created_at,
          author: article.author || 'Nadir Khan',
          tags: [article.category]
        }}
      />
      <ArticleSchema 
        title={article.title}
        description={article.excerpt}
        datePublished={article.publication_date || article.created_at}
        author={article.author || 'Nadir Khan'}
        url={`https://agilitie.com.au/insights/${slug}`}
      />
      {/* Hero */}
      <section className="py-16 md:py-24 bg-[#faf9f7]" data-testid="article-hero">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <button 
            onClick={() => navigate('/insights')}
            className="inline-flex items-center gap-2 text-[#4a4744] font-dm-sans text-sm mb-8 hover:text-[#d4782c] transition-colors duration-200"
            data-testid="back-to-insights"
          >
            <ArrowLeft size={16} />
            Back to Insights
          </button>
          
          <div className="flex items-center gap-4 mb-6">
            <span className="text-[#d4782c] font-dm-sans text-sm font-semibold uppercase tracking-wider">
              {article.category}
            </span>
            <span className="text-[#4a4744] font-dm-sans text-sm">
              {formatDate(article.publication_date || article.created_at)}
            </span>
          </div>
          
          <h1 
            className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a1a1a] mb-6 leading-tight"
            data-testid="article-title"
          >
            {article.title}
          </h1>
          
          <p className="text-[#4a4744] text-lg leading-relaxed">
            {article.excerpt}
          </p>
          
          {article.author && (
            <p className="mt-6 text-[#4a4744] font-dm-sans text-sm">
              By <span className="font-semibold text-[#1a1a1a]">{article.author}</span>
            </p>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-20 bg-white" data-testid="article-content">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div 
            className="prose prose-lg max-w-none article-content"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
          />
          
          {/* LinkedIn Attribution */}
          {article.linkedin_url && (
            <div className="mt-12 pt-8 border-t border-[#e5e2dc]" data-testid="linkedin-attribution">
              <a 
                href={article.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#4a4744] hover:text-[#d4782c] font-dm-sans text-sm transition-colors duration-200"
              >
                <LinkedInIcon />
                <span>Originally published on LinkedIn</span>
                <span className="text-xs">↗</span>
              </a>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#faf9f7]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-4">
            Ready to create the conditions for meaningful change?
          </h2>
          <p className="text-[#4a4744] mb-8">
            We'd love to understand your challenges and explore how we can help.
          </p>
          <Link 
            to="/contact" 
            className="btn-primary inline-block"
            data-testid="article-cta-button"
          >
            Start a Conversation
          </Link>
        </div>
      </section>
    </Layout>
  );
}
